"use client";

import React from "react";

interface AIDICSDiagramProps {
  activeStep: number;
  onStepClick?: (index: number) => void;
}

/**
 * AIDICS framework diagram.
 *
 * Faithful port of the live e-cq.net "aid-ics-map" section: each circle is two
 * ring layers (a full gray ring with dots + an arc outline) that slowly rotate
 * in opposite directions on hover (transition 5s), a dark centre disc with the
 * step letter, and an alternating top / bottom label connector
 * (penguin stem+dot + crash-line dash). Assets copied from the original site.
 */

const ASSET = "/images/OTSecurity/aid-ics";

const steps = [
  { letter: "A", name: "Assess" },
  { letter: "I", name: "Identify" },
  { letter: "D", name: "Design" },
  { letter: "I", name: "Inspect" },
  { letter: "C", name: "Coach" },
  { letter: "S", name: "Simulate" },
];

const AIDICSDiagram: React.FC<AIDICSDiagramProps> = ({ onStepClick }) => {
  return (
    <div className="aidics-map">
      {steps.map((step, index) => (
        <div
          key={`${step.letter}-${index}`}
          className="aidics-item"
          onClick={() => onStepClick?.(index)}
        >
          <div className="aidics-wrap">
            <span className="aidics-outline" aria-hidden />
            <span className="aidics-border" aria-hidden />
            {/* Connector: crisp vector fork + stem + dot. Figma node 1800:55349
                is a 20×64 group — two 2.761×13.807 rounded bars at ±45°, a
                2×45 stem and a 13px dot, all solid #EC1938. */}
            <svg
              className="aidics-penguin"
              viewBox="0 0 20 64"
              fill="none"
              aria-hidden
            >
              <path
                d="M1.4 1.4 L10 11 L18.6 1.4"
                stroke="#EC1938"
                strokeWidth="2.761"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <line
                x1="10"
                y1="9"
                x2="10"
                y2="52"
                stroke="#EC1938"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <circle cx="10" cy="57.5" r="6.5" fill="#EC1938" />
            </svg>
            <span className="aidics-crash" aria-hidden>
              <i className="aidics-crash-long" />
              <i className="aidics-crash-short" />
            </span>
            <div className="aidics-char">
              <span>{step.letter}</span>
            </div>
          </div>
          <div className="aidics-title">{step.name}</div>
        </div>
      ))}

      <style jsx>{`
        .aidics-map {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 32px;
          width: 100%;
          /* Figma node 1800:55251 — the diagram group is 1201×408 centred in the
             1300px column: 6×~172px rings + 5×32px gaps, with the label/connector
             band taking 115px above and 122px below the ring row. */
          max-width: 1201px;
          margin: 0 auto;
          padding: 115px 0 122px;
        }
        .aidics-item {
          position: relative;
          width: 100%;
          cursor: pointer;
        }
        .aidics-wrap {
          position: relative;
          padding-top: 100%;
        }

        /* Two rotating ring layers */
        .aidics-outline,
        .aidics-border {
          position: absolute;
          background-position: center;
          background-repeat: no-repeat;
          background-size: contain;
          transition: transform 5s;
        }
        .aidics-outline {
          top: 50%;
          left: 50%;
          width: 100%;
          height: 100%;
          z-index: 1;
          background-image: url("${ASSET}/pentest-outline.svg");
        }
        .aidics-border {
          top: 51%;
          left: 51%;
          width: calc(100% - 30px);
          height: calc(100% - 30px);
          z-index: 2;
          background-image: url("${ASSET}/pentest-border.svg");
        }

        /* Centre disc + letter */
        .aidics-char {
          position: absolute;
          top: 50%;
          left: 50%;
          /* Figma: 126px dark disc inside a 171px ring. */
          width: 73.7%;
          height: 73.7%;
          background: #0d1117;
          border-radius: 50%;
          transform: translate(-50%, -50%);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 3;
        }
        .aidics-char span {
          font-family: var(--font-avenir), sans-serif;
          font-size: 60px;
          font-weight: 700;
          line-height: 1;
          color: #ec1938;
        }

        /* Label + connector */
        .aidics-title {
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          color: #ec1938;
          font-family: var(--font-avenir), sans-serif;
          font-size: 24px;
          font-weight: 600; /* Figma: Avenir Next Demi Bold */
          line-height: 1.375; /* Figma text box 33px at 24px; scales at the
                                 smaller breakpoints below */
          white-space: nowrap;
          text-align: center;
        }
        .aidics-penguin {
          position: absolute;
          width: 20px;
          height: 64px;
          overflow: visible;
        }
        /* Dash above / below the label — Figma Rectangle 8 + 9. */
        .aidics-crash {
          position: absolute;
          display: flex;
          align-items: center;
          gap: 5px;
        }
        .aidics-crash-long {
          display: block;
          width: 45.5px;
          height: 2.8px;
          background: #ec1938;
          border-radius: 2px;
        }
        .aidics-crash-short {
          display: block;
          width: 7px;
          height: 2.8px;
          background: #ec1938;
          border-radius: 2px;
        }

        /* Odd items (Assess / Design / Coach) — label above */
        .aidics-item:nth-child(odd) .aidics-outline {
          transform: translate(-50%, -50%) rotate(-45deg);
        }
        .aidics-item:nth-child(odd) .aidics-border {
          transform: translate(-49%, -53%) rotate(-145deg);
        }
        .aidics-item:nth-child(odd):hover .aidics-outline {
          transform: translate(-50%, -50%) rotate(-270deg);
        }
        .aidics-item:nth-child(odd):hover .aidics-border {
          transform: translate(-50%, -50%) rotate(10deg);
        }
        /* Offsets are % of the ring box (174px at 1440) so the whole cluster
           scales down with the grid. Figma anchors, measured from the ring:
           connector 81px above, label box 113px above, dash 112px above. */
        .aidics-item:nth-child(odd) .aidics-penguin {
          top: -46.5%;
          left: 50%;
          transform: translateX(-50%);
        }
        .aidics-item:nth-child(odd) .aidics-title {
          top: -65%;
          left: 50%;
          transform: translateX(-50%);
        }
        .aidics-item:nth-child(odd) .aidics-crash {
          top: -64.5%;
          left: 50%;
          transform: translateX(-50%);
        }

        /* Even items (Identify / Inspect / Simulate) — label below */
        .aidics-item:nth-child(even) .aidics-outline {
          transform: translate(-50%, -50%) rotate(150deg);
        }
        .aidics-item:nth-child(even) .aidics-border {
          transform: translate(-49%, -51%) rotate(-45deg);
        }
        .aidics-item:nth-child(even):hover .aidics-outline {
          transform: translate(-50%, -50%) rotate(-120deg);
        }
        .aidics-item:nth-child(even):hover .aidics-border {
          transform: translate(-50%, -50%) rotate(140deg);
        }
        .aidics-item:nth-child(even) .aidics-penguin {
          bottom: -46.5%;
          left: 50%;
          transform: translateX(-50%) rotate(180deg);
        }
        .aidics-item:nth-child(even) .aidics-title {
          bottom: -61.5%;
          left: 50%;
          transform: translateX(-50%);
        }
        .aidics-item:nth-child(even) .aidics-crash {
          bottom: -63.7%;
          left: 50%;
          transform: translateX(-50%);
        }

        /* 992-1335px: the map is squeezed below its designed 1201px, so the
           rotated rings have no slack to hang into and the page scrolls
           sideways (measured 1035px of content in a 1024px viewport). Reserving
           40px a side is a no-op once the column can hold 1281px, so the 1201px
           design width and its ~172px rings are untouched on a wide desktop.
           Scoped to min-width 992px so it cannot interact with the padding the
           two blocks below apply in the phone and tablet bands. */
        @media (min-width: 992px) {
          .aidics-map {
            max-width: min(1201px, calc(100% - 80px));
          }
        }

        @media (max-width: 991px) {
          .aidics-map {
            /* Same rotated-ring overflow as the 767px block below, which this
               band shares. Measured: 784px of content in a 768px viewport.
               Needed inset is 3.82% of the page content width (solve
               P >= 0.0414 * (C - 2P) for P), so 36px at the 991px top of this
               band; 40px keeps a margin. */
            padding: 70px 40px;
          }
          .aidics-char span {
            font-size: 30px;
          }
          .aidics-title {
            font-size: 16px;
          }
        }
        @media (max-width: 767px) {
          .aidics-map {
            gap: 4px;
            /* 16px of horizontal inset removes the page's only real overflow.
               Both ring layers are square boxes rotated 45deg, so each paints a
               bounding box 1.414x its side and (1.414 - 1) / 2 = 20.7% of the
               cell hangs off each edge. At a 390px viewport the six cells are
               (358 - 20) / 6 = 56.3px, so the outer rings reach 11.7px past the
               content box. Trimming 32px total makes the cells 51px (overhang
               10.6px) and pulls the grid edge 16px inboard: 17.1px less reach.
               Breakpoint note: this component's whole responsive system runs on
               767/991px, and the 640-767px range overflows today too, so fixing
               it here is a strict improvement. Splitting one file across two
               breakpoint systems (767 here, Tailwind's 640 elsewhere) would be
               worse than keeping the existing edge. */
            /* 24px, not 16px: 16px clears a 390px viewport but leaves overflow
               at the 640px top of this band, where the cells are widest. */
            padding: 40px 24px;
          }
          .aidics-char span {
            font-size: 15px;
          }
          .aidics-title {
            font-size: 12px;
          }
          .aidics-penguin,
          .aidics-crash {
            display: none;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .aidics-outline,
          .aidics-border {
            transition: none;
          }
        }
      `}</style>
    </div>
  );
};

export default AIDICSDiagram;
